import { useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import dayjs from 'dayjs';
import { Button, ButtonType } from '@nx-frontend/shared/ui/button';
import { Spinner } from '@nx-frontend/shared/ui/spinner';
import { useDebounce } from '@nx-frontend/shared/hooks';
import { BillingAddonCode } from '@nx-frontend/shared/types';
import { BillingAddon } from '@nx-frontend/shared/types/paywall-types';
import { GenerativeAiAddonCard } from './generative-ai-addon-card';
import { PaywallAddonCard } from './paywall-addon-card';
import { $paywallStore, changeAddonCountEvent, confirmSubscriptionEvent, fetchOrderSummaryEvent } from './store';
import { useGetAddonUnitsString, useGetAddonPricePreview } from './util';

interface AddonFormProps {
  code: BillingAddonCode;
}

export function AddonForm({ code }: AddonFormProps) {
  const paywallStore = useUnit($paywallStore);
  const [changeAddonCount, fetchOrderSummary, confirmSubscription] = useUnit([
    changeAddonCountEvent,
    fetchOrderSummaryEvent,
    confirmSubscriptionEvent,
  ]);

  const addon = paywallStore.addons.find((addon) => addon.code === code);
  const selectedAddon = (paywallStore.selectedAddons as Record<BillingAddonCode, BillingAddon>)[code];
  // important to use useCallback for debounced functions
  // if not, it will be recreated on every render
  const updatePlanCallback = useCallback(() => {
    fetchOrderSummary();
  }, [fetchOrderSummary]);

  const debouncedUpdatePlan = useDebounce<void>(updatePlanCallback, 600);

  const handleCounterChange = useCallback(
    (quantity: number) => {
      changeAddonCount({ code, quantity });
      debouncedUpdatePlan();
    },
    [changeAddonCount, code, debouncedUpdatePlan]
  );

  const onConfirmSubscription = useCallback(() => {
    confirmSubscription();
  }, [confirmSubscription]);

  const t = useTranslations('features');

  const getAddonPricePreview = useGetAddonPricePreview();
  const getAddonUnitsString = useGetAddonUnitsString();

  const { pricePerMonth } = addon ?? { pricePerMonth: 0 };

  const billingPlan = paywallStore.billingPlans.find((plan) => plan.code === paywallStore.currentPlanCode);

  const preview = useMemo(() => {
    const selectedAddons = paywallStore.selectedAddons as Record<BillingAddonCode, BillingAddon>;
    const quantity = selectedAddons[code]?.quantity || 0;
    const priceForAddon = (addon?.price || 0) * quantity;

    const total = parseFloat((paywallStore.currentSubscriptionTotal + priceForAddon).toFixed(2));
    let totalWidthDiscount = total;
    if (paywallStore.foreverCouponsDiscount) {
      totalWidthDiscount = +(total - (total * paywallStore.foreverCouponsDiscount) / 100).toFixed(2);
    }

    const preview = getAddonPricePreview(code, {
      price: paywallStore.billedToday ?? 0,
      total: totalWidthDiscount,
      plan: billingPlan ? billingPlan.name.en : undefined,
      date: paywallStore.currentPeriodEndsAt
        ? dayjs(new Date(paywallStore.currentPeriodEndsAt)).format('MMM D, YYYY')
        : undefined,
    });

    return preview;
  }, [
    code,
    addon,
    paywallStore.selectedAddons,
    paywallStore.currentSubscriptionTotal,
    billingPlan,
    paywallStore.billedToday,
    paywallStore.currentPeriodEndsAt,
    paywallStore.foreverCouponsDiscount,
    getAddonPricePreview,
  ]);

  return (
    <div className="w-full mt-9">
      {addon ? (
        <>
          {code === BillingAddonCode.AI ? (
            <GenerativeAiAddonCard
              members={paywallStore.onlyMembersCnt}
              price={pricePerMonth}
              description={getAddonUnitsString(code)}
              total={paywallStore.billedToday}
            />
          ) : (
            <PaywallAddonCard
              code={code}
              price={pricePerMonth * (selectedAddon?.quantity ?? 0)}
              perLabel={
                code === BillingAddonCode.PORTALS ? t('paywall.month_per_portal') : t('paywall.month_per_addon')
              }
              description={getAddonUnitsString(code, { count: selectedAddon?.quantity ?? 0 })}
              showCounter={true}
              selectable={false}
              value={1}
              onCounterChange={handleCounterChange}
              min={1}
            />
          )}
          <Button
            type={ButtonType.PRIMARY}
            disabled={
              paywallStore.fetchingOrderSummary || !selectedAddon?.quantity || paywallStore.fetchingConfirmSubscription
            }
            className="w-full mt-4"
            onClick={onConfirmSubscription}
          >
            {t('paywall.confirm')}
          </Button>
          <div className="text-neutral-400 text-xs mt-2 min-h-[50px]">
            {paywallStore.fetchingOrderSummary ? (
              <div className="w-full flex items-center justify-center">
                <Spinner width={32} height={32} />
              </div>
            ) : (
              preview
            )}
          </div>
        </>
      ) : (
        <>
          {t.rich('payrall.something_went_wrong_contact_us', {
            link: (chunk) => (
              <a href="https://thefusebase.com/support" className="text-cyan-900" target="_blank" rel="noreferrer">
                {chunk}
              </a>
            ),
          })}
        </>
      )}
    </div>
  );
}
